import { resolveYesNoOptionPair } from '../primitives/yesNoRadio';
import type { Question } from '../../types';
import { flattenQuestionTree } from '../../walkQuestionTree';
import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import { walkLayoutStructureChildren } from './layoutStructureChildLists';
import type { LayoutNode } from './types';

/**
 * For each `qid`, the set of coded `value`s declared on `radio-option` nodes anywhere
 * in the layout (tables, both `conditional` branches, etc.).
 *
 * `OASISRadio` is not walked — those controls use schema `values` implicitly.
 */
export function collectLayoutRadioOptionValuesByQid(
  nodes: LayoutNode[]
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();

  const add = (qid: string, value: string) => {
    const v = value.trim();
    if (!v) return;
    let set = map.get(qid);
    if (!set) {
      set = new Set<string>();
      map.set(qid, set);
    }
    set.add(v);
  };

  const walk = (arr: LayoutNode[]) => {
    for (const node of arr) {
      switch (node.type) {
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'divider':
        case 'static':
        case 'text':
        case 'display':
        case 'external-answer':
        case 'section-label':
        case 'sum':
        case 'table':
        case 'conditional':
        case 'disabled-when':
        case 'repeat':
        case 'OASISMultiselect':
        case 'OASISMultiselectOption':
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISPhysician':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
          break;
        case 'radio-option':
          add(node.qid, node.value);
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }
      walkLayoutStructureChildren(node, walk);
    }
  };

  walk(nodes);
  return map;
}

/** QIDs rendered via `OASISYesNoCheckbox` anywhere in the layout tree. */
export function collectLayoutYesNoCheckboxQids(
  nodes: LayoutNode[]
): Set<string> {
  const qids = new Set<string>();

  const walk = (arr: LayoutNode[]) => {
    for (const node of arr) {
      if (node.type === 'OASISYesNoCheckbox') {
        qids.add(node.qid);
      }
      walkLayoutStructureChildren(node, walk);
    }
  };

  walk(nodes);
  return qids;
}

/**
 * Partial radio values declared in layout: explicit `radio-option` cells plus
 * `OASISYesNoCheckbox` (treated as declaring only the schema Yes value).
 */
export function collectLayoutPartialRadioValuesByQid(
  nodes: LayoutNode[],
  sectionQuestions: Question[]
): Map<string, Set<string>> {
  const map = collectLayoutRadioOptionValuesByQid(nodes);
  const questionsByQid = new Map(
    flattenQuestionTree(sectionQuestions).map(q => [q.qid, q])
  );

  for (const qid of collectLayoutYesNoCheckboxQids(nodes)) {
    const pair = resolveYesNoOptionPair(questionsByQid.get(qid)?.values);
    if (!pair) continue;

    let set = map.get(qid);
    if (!set) {
      set = new Set<string>();
      map.set(qid, set);
    }
    set.add(pair.yesValue);
  }

  return map;
}
